import axios from 'axios';

const basePath = (siteId) => `/api/site-control/sites/${siteId}`;
const authConfig = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

export const loadSiteControlSnapshot = async (siteId) => {
  const response = await axios.get(basePath(siteId), authConfig());
  return response.data || null;
};

export const loadDeviceConfigSnapshot = async (siteId) => {
  const response = await axios.get(`${basePath(siteId)}/device-config`, { ...authConfig(), timeout: 10000 });
  return response.data || null;
};

const androidConfigPath = (siteId, targetId) => `${basePath(siteId)}/targets/${targetId}/android-config`;
export const readAndroidConfig = async (siteId, targetId) => (await axios.get(androidConfigPath(siteId, targetId),
  { ...authConfig(), timeout: 65000 })).data;
export const loadAndroidConfigJobs = async (siteId, targetId) => (await axios.get(`${androidConfigPath(siteId, targetId)}/jobs`,
  { ...authConfig(), timeout: 10000 })).data;
export const startAndroidConfig = async (siteId, targetId, payload) => (await axios.post(androidConfigPath(siteId, targetId), payload,
  { ...authConfig(), timeout: 10000 })).data;
export const loadAndroidConfigJob = async (siteId, targetId, jobId) => (await axios.get(`${androidConfigPath(siteId, targetId)}/jobs/${jobId}`,
  { ...authConfig(), timeout: 10000 })).data;
export const findAndroidConfigRequest = async (siteId, targetId, requestKey) => (await axios.get(`${androidConfigPath(siteId, targetId)}/requests/${requestKey}`,
  { ...authConfig(), timeout: 10000 })).data;

export const restartDeviceConfigTarget = async (siteId, targetDeviceId, payload) => {
  const response = await axios.post(`${basePath(siteId)}/targets/${targetDeviceId}/restart`, payload,
    { ...authConfig(), timeout: 10000 });
  return response.data || null;
};

export const checkDeviceConfigRestartConnection = async (siteId, jobId) => {
  const response = await axios.post(`${basePath(siteId)}/device-config/jobs/${jobId}/check-connection`, {},
    { ...authConfig(), timeout: 60000 });
  return response.data || null;
};

export const updateSiteControlBridges = async (siteId, payload) => {
  const response = await axios.put(`${basePath(siteId)}/bridges`, payload, authConfig());
  return response.data || null;
};

export const scanSiteControlNetwork = async (siteId, payload) => {
  const response = await axios.post(`${basePath(siteId)}/scan`, payload, authConfig());
  return response.data || null;
};

export const createSiteControlTarget = async (siteId, payload) => {
  const response = await axios.post(`${basePath(siteId)}/targets`, payload, authConfig());
  return response.data || null;
};

export const confirmSiteControlRelocation = async (siteId, targetDeviceId, payload) => {
  const response = await axios.post(
    `${basePath(siteId)}/targets/${targetDeviceId}/relocate`,
    payload,
    authConfig()
  );
  return response.data || null;
};

export const updateSiteControlTarget = async (siteId, targetDeviceId, payload) => {
  const response = await axios.put(`${basePath(siteId)}/targets/${targetDeviceId}`, payload, authConfig());
  return response.data || null;
};

export const deleteSiteControlTarget = async (siteId, targetDeviceId) => {
  const response = await axios.delete(`${basePath(siteId)}/targets/${targetDeviceId}`, authConfig());
  return response.data || null;
};

export const startSiteControlPairing = async (siteId, targetDeviceId, payload) => {
  const response = await axios.post(`${basePath(siteId)}/targets/${targetDeviceId}/pair/start`, payload, authConfig());
  return response.data || null;
};

export const confirmSiteControlPairing = async (siteId, targetDeviceId, payload) => {
  const response = await axios.post(`${basePath(siteId)}/targets/${targetDeviceId}/pair/confirm`, payload, authConfig());
  return response.data || null;
};

export const testSiteControlTarget = async (siteId, targetDeviceId, payload) => {
  const response = await axios.post(`${basePath(siteId)}/targets/${targetDeviceId}/test`, payload, authConfig());
  return response.data || null;
};

export const sendSiteControlAction = async (siteId, targetDeviceId, payload) => {
  const response = await axios.post(`${basePath(siteId)}/targets/${targetDeviceId}/action`, payload, authConfig());
  return response.data || null;
};

export const loadDeviceLogCaptures = async (siteId) => (await axios.get(`${basePath(siteId)}/diagnostics`,
  { ...authConfig(), timeout: 10000 })).data;
export const captureDeviceLogs = async (siteId, targetId, payload) => (await axios.post(`${basePath(siteId)}/diagnostics/targets/${targetId}`,
  payload, { ...authConfig(), timeout: 10000 })).data;
export const saveDeviceLogsToServer = async (siteId, payload) => (await axios.post(`${basePath(siteId)}/diagnostics/save`,
  payload, { ...authConfig(), timeout: 30000 })).data;
export const readDeviceLogPage = async (siteId, id, offset) => (await axios.get(`${basePath(siteId)}/diagnostics/${id}/content`,
  { ...authConfig(), params: { offset }, timeout: 10000 })).data;
export const downloadDeviceLog = async (siteId, id) => (await axios.get(`${basePath(siteId)}/diagnostics/${id}/download`,
  { ...authConfig(), responseType: 'blob', timeout: 15000 })).data;
export const cancelDeviceLog = async (siteId, id) => (await axios.post(`${basePath(siteId)}/diagnostics/${id}/cancel`, {}, authConfig())).data;
export const deleteDeviceLog = async (siteId, id) => (await axios.delete(`${basePath(siteId)}/diagnostics/${id}`, authConfig())).data;

export const loadDeviceApkUpdates = async (siteId) => (await axios.get(`${basePath(siteId)}/apk-updates`, { ...authConfig(), timeout: 10000 })).data;
export const loadDeviceApkCatalog = async (siteId, channel, variant) => (await axios.get(`${basePath(siteId)}/apk-updates/catalog`,
  { ...authConfig(), params: { channel, ...(variant ? { variant } : {}) }, timeout: 125000 })).data;
export const reviewDeviceApkUpdate = async (siteId, targetId, payload) => (await axios.post(`${basePath(siteId)}/apk-updates/targets/${targetId}/review`,
  payload, { ...authConfig(), timeout: 10000 })).data;
export const confirmDeviceApkUpdates = async (siteId, payload) => (await axios.post(`${basePath(siteId)}/apk-updates/confirm`, payload,
  { ...authConfig(), timeout: 10000 })).data;
export const cancelDeviceApkUpdate = async (siteId, id) => (await axios.post(`${basePath(siteId)}/apk-updates/${id}/cancel`, {}, authConfig())).data;
export const verifyDeviceApkUpdate = async (siteId, id) => (await axios.post(`${basePath(siteId)}/apk-updates/${id}/verify`, {}, authConfig())).data;
